import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  type MDXEditorMethods,
  quotePlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import "./markdownEditor.css";
import { memo, useCallback, useEffect, useRef } from "react";

type MarkdownEditorProps = {
  value: string;
  resetKey?: string | number;
  onChange: (value: string) => void;
  hasError?: boolean;
  placeholder?: string;
};

const URL_SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;

const toolbarContents = () => (
  <>
    <BlockTypeSelect />
    <BoldItalicUnderlineToggles />
    <ListsToggle />
    <CreateLink />
  </>
);

export const markdownToPlainText = (markdown: string) =>
  markdown
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^>\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/[*_~]/g, "")
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeLinkUrl = (url: string) => {
  const trimmedUrl = url.trim();

  if (
    !trimmedUrl ||
    URL_SCHEME_PATTERN.test(trimmedUrl) ||
    trimmedUrl.startsWith("//") ||
    trimmedUrl.startsWith("/") ||
    trimmedUrl.startsWith("#") ||
    trimmedUrl.startsWith("?")
  ) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

export const normalizeMarkdownLinkUrls = (markdown: string) =>
  markdown.replace(
    /(?<!!)(\[[^\]]*]\()([^)]+)(\))/g,
    (match, prefix: string, destinationWithMetadata: string, suffix: string) => {
      const parts = destinationWithMetadata.match(
        /^(\s*<?)([^>\s)]+)(>?(?:\s+.*)?)$/
      );

      if (!parts) {
        return match;
      }

      const [, leadingWhitespaceAndBracket, destination, trailingMetadata] =
        parts;

      return `${prefix}${leadingWhitespaceAndBracket}${normalizeLinkUrl(
        destination
      )}${trailingMetadata}${suffix}`;
    }
  );

// Intentionally omit imagePlugin so pasted or toolbar-driven image uploads stay disabled.
const editorPlugins = [
  headingsPlugin({ allowedHeadingLevels: [1, 2, 3] }),
  listsPlugin(),
  quotePlugin(),
  linkPlugin(),
  linkDialogPlugin(),
  markdownShortcutPlugin(),
  toolbarPlugin({ toolbarContents }),
];

function MarkdownEditor({
  value,
  resetKey,
  onChange,
  hasError = false,
  placeholder,
}: MarkdownEditorProps) {
  const editorRef = useRef<MDXEditorMethods>(null);

  useEffect(() => {
    editorRef.current?.setMarkdown(value);
  }, [resetKey]);

  const handleChange = useCallback(
    (nextMarkdown: string) => {
      const normalizedMarkdown = normalizeMarkdownLinkUrls(nextMarkdown);

      if (normalizedMarkdown !== nextMarkdown) {
        editorRef.current?.setMarkdown(normalizedMarkdown);
      }

      onChange(normalizedMarkdown);
    },
    [onChange]
  );

  return (
    <div
      className={`overflow-hidden rounded-md border bg-white shadow-sm transition focus-within:shadow-md ${
        hasError ? "border-rose-200" : "border-slate-200"
      }`}
    >
      <MDXEditor
        ref={editorRef}
        markdown={value}
        trim={false}
        onChange={handleChange}
        placeholder={placeholder}
        className="mdxeditor curated-directory-markdown-editor"
        contentEditableClassName="min-h-[10rem] px-3 py-2 text-sm leading-6 text-blue-300 focus:outline-none [&_p]:my-0 [&_p+*]:mt-3 [&_h1]:my-0 [&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:leading-tight [&_h1]:text-blue-400 [&_h2]:my-0 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:leading-tight [&_h2]:text-blue-300 [&_h3]:my-0 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:leading-snug [&_h3]:text-blue-300 [&_ul]:my-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_blockquote]:my-0 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-grey-200 [&_a]:text-blue-300 [&_a]:underline [&_a]:underline-offset-2"
        plugins={editorPlugins}
      />
    </div>
  );
}

export default memo(MarkdownEditor);
